import { label, number } from "../../config";
import store from "../../store";
import { getTagLabelAndColor } from "./tasks.constants";
import { pinTasks } from "./tasks.service";

/**
 * Converts a value to an array if it is not already an array.
 * @param {unknown} value
 * @returns {Array}
 */
const asTaskArray = (value) => (Array.isArray(value) ? value : []);

/**
 * updates grid data
 * @param {Object} updatedData
 * @author Himanshu Negi
 */
export const updateGridTasks = (updatedData) => {
  const state = store.getState();
  const { allTasks } = state.tasks;
  const newMyTask = allTasks?.myTasks?.map((task) => {
    const data = updatedData?.find((data) => data.Id === task?.Id);
    if (data) {
      return { ...task, ...data };
    } else {
      return { ...task };
    }
  });

  const newProjectTask = allTasks?.projectTasks?.map((task) => {
    const data = updatedData?.find((data) => data.Id === task.Id);
    if (data) {
      return { ...task, ...data };
    } else {
      return { ...task };
    }
  });

  const newSuperSetTask = allTasks?.superset?.map((task) => {
    const data = updatedData?.find((data) => data.Id === task.Id);
    if (data) {
      return { ...task, ...data };
    } else {
      return { ...task };
    }
  });

  return { newMyTask, newProjectTask, newSuperSetTask };
};

/**
 * checks for assignee changed
 * @param {Object} updatedData
 * @author Himanshu Negi
 */
export const isAssigneeChanged = (updatedData) => {
  const state = store.getState();
  let isChanged = false;
  const { allTasks } = state.tasks;
  const { user } = state.auth;
  allTasks?.superset?.forEach((task) => {
    const data = updatedData?.find((data) => data.Id === task?.Id);
    if (data) {
      if (data.Assignee !== task?.Assignee) {
        isChanged = true;
      }
    } else if (updatedData?.find((d) => d.Assignee === user?.id)) {
      isChanged = true;
    }
  });
  return isChanged;
};

export const getUserPositionPayload = (projectId) => {
  const state = store.getState();
  if (projectId) {
    return {
      positionId: state?.userPosition?.positionId,
      userId: state.auth?.user?.id,
      routeId: state?.tasks?.defaultDetails?.routeTypeList?.find((route) => route.Id === number.TWO)?.Id,
      entityId: projectId,
    };
  } else {
    return {
      positionId: state?.userPosition?.positionId,
      userId: state.auth?.user?.id,
      routeId: state?.tasks?.defaultDetails?.routeTypeList?.find((route) => route.Id === number.ONE)?.Id,
      entityId: state.auth?.user?.myProjectId,
    };
  }
};

/**
 * on pinning/unpinning returns response and pinTaskCount for respective task
 */
export const pinTaskHelper = async (payload) => {
  let res = await pinTasks(payload);
  let pinTaskCount = Array.isArray(res) ? res[number.ZERO][number.ZERO].pinnedTaskCount : null;
  return [res, pinTaskCount];
};

/**
 * Filters and maps the `gridAllTask` data based on the selected `filterTableColumn`.
 * @author Bhavana
 */
export const getFilteredGridData = (gridAllTask, filterTableColumn, projectId) => {
  return gridAllTask.map((task) => {
    const filteredData = {};

    if (filterTableColumn.includes(label?.TASK_ID)) {
      filteredData.ProjectTaskId = task?.ProjectTaskId;
    }

    filteredData.Name = task?.Name;

    if (filterTableColumn.includes(label?.STAGE)) {
      filteredData.StageName = task?.StageName;
    }

    if (filterTableColumn.includes(label?.DUE_DATE)) {
      filteredData.date = task?.date;
    }

    if (filterTableColumn.includes(label?.CHOOSE_PRIORITY)) {
      filteredData.PriorityName = task?.PriorityName;
    }

    if (projectId && filterTableColumn.includes(label?.CURRENT_USER)) {
      filteredData.AssigneeName = task?.AssigneeName;
    }

    if (filterTableColumn.includes(label?.TAGS)) {
      let { labels } = getTagLabelAndColor(task?.tagIds);
      filteredData.Tags = labels;
    } else if (filterTableColumn.includes(label?.ASSIGNED_BY)) {
      filteredData.AssignedByName = task?.AssignedByName;
    }

    return filteredData;
  });
};

/**
 * Returns the label of the filter matching the last filter ID.
 * @author Bhavana
 */
export const getFilterName = () => {
  const state = store.getState();
  const { lastFilterId, teamSavedFilters } = state.teamFilter;
  return teamSavedFilters?.find((filter) => filter.value === lastFilterId && lastFilterId)?.label;
};

/**
 * update the stage of task in redux
 * @param {updatedStage , task} object
 * @author Sejal
 */
export const setStageName = (allTasks, updatedStage, id) => {
  const newMyTasks = asTaskArray(allTasks?.myTasks).map((taskData) =>
    taskData.Id == id ? { ...taskData, StageName: updatedStage.label } : taskData
  );
  const newProjectTasks = asTaskArray(allTasks?.projectTasks).map((taskData) =>
    taskData.Id == id ? { ...taskData, StageName: updatedStage.label } : taskData
  );
  return { newMyTasks, newProjectTasks };
};

/**
 * update the due date of task in redux
 * @param {updatedDate , alltasks , id}
 * @author Sejal
 */
export const setDueDate = (allTasks, updatedDate, id) => {
  const newMyTasks = asTaskArray(allTasks?.myTasks).map((taskData) => (taskData.Id == id ? { ...taskData, DueDate: updatedDate } : taskData));
  const newProjectTasks = asTaskArray(allTasks?.projectTasks).map((taskData) => (taskData.Id == id ? { ...taskData, DueDate: updatedDate } : taskData));
  return { newMyTasks, newProjectTasks };
};

/**
 * update the name of task in redux
 * @param {allTasks, TaskName, id}
 * @author Sejal
 */
export const setName = (allTasks, TaskName, id) => {
  const newMyTasks = asTaskArray(allTasks?.myTasks).map((taskData) => (taskData.Id == id ? { ...taskData, Name: TaskName } : taskData));
  const newProjectTasks = asTaskArray(allTasks?.projectTasks).map((taskData) => (taskData.Id == id ? { ...taskData, Name: TaskName } : taskData));
  return { newMyTasks, newProjectTasks };
};

/**
 * update the Priority of task in redux
 * @param {allTasks,  Priority, PriorityName, id}
 * @author Sejal
 */
export const updatePriority = (allTasks, Priority, PriorityName, id) => {
  const newMyTasks = asTaskArray(allTasks?.myTasks).map((taskData) =>
    taskData.Id == id ? { ...taskData, Priority: Priority, PriorityName: PriorityName } : taskData
  );
  const newProjectTasks = asTaskArray(allTasks?.projectTasks).map((taskData) =>
    taskData.Id == id ? { ...taskData, Priority: Priority, PriorityName: PriorityName } : taskData
  );
  return { newMyTasks, newProjectTasks };
};

/**
 * update the tags of task in redux
 * @param {allTasks, tagIds, id}
 * @author Sejal
 */
export const updateTag = (allTasks, tagIds, id) => {
  const newMyTasks = asTaskArray(allTasks?.myTasks).map((taskData) => (taskData.Id == id ? { ...taskData, tagIds: tagIds } : taskData));
  const newProjectTasks = asTaskArray(allTasks?.projectTasks).map((taskData) => (taskData.Id == id ? { ...taskData, tagIds: tagIds } : taskData));
  return { newMyTasks, newProjectTasks };
};

/**
 * update the assignment Id of task in redux
 * @param {allTasks, assigneeList, relatedAssignedId, assignedBy, id}
 * @author Sejal
 */
export const updateAssignmentId = (allTasks, assigneeList, relatedAssignedId, assignedBy, id) => {
  const myTasksList = asTaskArray(allTasks?.myTasks);
  const projectTasksList = asTaskArray(allTasks?.projectTasks);
  const newMyTasks = myTasksList.map((taskData) =>
    taskData.Id == id
      ? {
          ...taskData,
          Assignee: relatedAssignedId,
          AssignedByName: assigneeList?.find((name) => name.value === assignedBy)?.label,
          AssigneeName: assigneeList?.find((name) => name.value === relatedAssignedId)?.label,
          AssigneePhoto: assigneeList?.find((name) => name.value === relatedAssignedId)?.PhotoLinks?.small,
          AssignedByPhoto: assigneeList?.find((name) => name.value === assignedBy)?.PhotoLinks?.small,
        }
      : taskData
  );
  const newProjectTasks =
    allTasks?.myTasks?.length === number.ZERO
      ? projectTasksList.map((taskData) =>
          taskData.Id == id
            ? {
                ...taskData,
                Assignee: relatedAssignedId,
                AssignedByName: assigneeList?.find((name) => name.value === assignedBy)?.label,
                AssigneeName: assigneeList?.find((name) => name.value === relatedAssignedId)?.label,
                AssigneePhoto: assigneeList?.find((name) => name.value === relatedAssignedId)?.PhotoLinks?.small,
                AssignedByPhoto: assigneeList?.find((name) => name.value === assignedBy)?.PhotoLinks?.small,
              }
            : taskData
        )
      : projectTasksList.filter((taskData) => taskData.Id != id);

  return { newMyTasks, newProjectTasks };
};
